/**
 * 頁 13／23 — 每週倉租（`ui-spec` §2 頁 13；P1-11 起；**P1.5-A 加物流唯讀版**）
 *
 * 按**結算日**（`settings.fee.settlement_weekday`；`decisions/019` 起 ＝ 逢星期三）結算物流倉租：期初 cbm（計費基礎）→ 儲存費 ＝ 期初 × 費率；
 * 入庫手續費 ＝ 該週收貨批次手續費合計。確認之後金額唔准再改。
 *
 * P1.5-A（`decisions/015` §2.4）——產品負責人 2026-09-28：
 *   · ⭐ **物流要核對 cbm 同價錢** → 加 **逐個 SKU 嘅期初 cbm 明細**（`openingByItem`）
 *   · ⭐ 物流**冇權限改**任何數字 → 只可以撳「**確認冇問題**」（`ack`；記 who／幾時／備註）
 *   · 產生／確認（改）一律只 `staff`／`admin`（後端 `generate`／`confirm` 亦 403）
 *
 * 紅線（同後端一致；前端先擋係為咗好體驗，**後端才係權威**）：
 *   · 金額／週界一律由 `@coeldery/storage-billing` 計，前端唔准自己寫公式
 *   · 費率一律由 `settings` 出（後端 `readFeeRates()` 缺 → 500 fail loud）
 */
import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { badge, btnGhost, btnPrimary, card, errText, hint as hintStyle } from '@coeldery/ui-kit';
import { ApiError, api, type Me, type WeeklyFee, type WeeklyFeesResponse } from '../lib/api';

/** cbm 用 6dp、金額用 2dp（同後端 `round6`／`round2` 一致） */
const fmt = (n: number, dp: number): string => Number(n).toFixed(dp);

/** 數字對齊右；cbm ／金額一律 monospace 好對位 */
const mono: React.CSSProperties = { fontVariantNumeric: 'tabular-nums', fontWeight: 700 };

export default function WeeklyFees({ user }: { user: Me }) {
  const { t } = useTranslation();
  /** ⭐ 只有內部（職員／管理員）可以產生／確認 —— 物流係唯讀 ＋ 只可以「核對後確認」 */
  const canManage = user.role === 'staff' || user.role === 'admin';
  const [data, setData] = useState<WeeklyFeesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await api.weeklyFees());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  const generate = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.generateWeeklyFee();
      setNotice(res.regenerated ? t('weeklyFee.regenerated') : t('weeklyFee.generated'));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  const confirm = async (fee: WeeklyFee) => {
    if (!window.confirm(t('weeklyFee.confirmAsk', { week: fee.weekStart }))) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.confirmWeeklyFee(fee.id);
      setNotice(t('weeklyFee.confirmedOk'));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  /** ⭐ P1.5-A：物流／內部「核對後確認」（**唔改任何金額**，只記 who／幾時／備註） */
  const ack = async (fee: WeeklyFee) => {
    const raw = window.prompt(t('weeklyFee.ackAsk', { week: fee.weekStart }));
    if (raw === null) return; // 取消
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      await api.ackWeeklyFee(fee.id, raw.trim() || undefined);
      setNotice(t('weeklyFee.ackOk'));
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : t('common.loadFailed'));
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <p style={hintStyle}>{t('common.loading')}</p>;

  const p = data?.preview;
  const rates = data?.rates;
  /** 期初 cbm 明細（只顯示有量嘅 SKU）*/
  const items = data?.openingByItem ?? [];

  return (
    <>
      <h2 style={{ margin: '0 0 6px', fontSize: '18px', fontWeight: 800 }}>{t('weeklyFee.title')}</h2>
      <p style={hintStyle}>{t('weeklyFee.intro')}</p>

      {error && <p style={errText} role="alert">{error}</p>}
      {notice && <p style={{ ...hintStyle, fontWeight: 700, color: 'var(--color-text)' }}>{notice}</p>}

      {/* 費率 ＋ 結算日（來源＝後台設定；缺 → 後端 500，唔會到呢度） */}
      {rates && (
        <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 800 }}>
            {t('weeklyFee.ratesTitle')}
          </h3>
          <p style={hintStyle}>
            {t('weeklyFee.storageRate')}：<span style={mono}>${fmt(rates.storagePerCbmWeek, 2)}</span>
            {'　'}
            {t('weeklyFee.handlingRate')}：<span style={mono}>${fmt(rates.handlingPerCbm, 2)}</span>
            {'　'}
            {t('weeklyFee.settlementWeekday')}：<span style={mono}>{rates.settlementWeekday}</span>
          </p>
          {data?.weekdayMismatch && (
            <p style={errText} role="alert">
              {t('weeklyFee.weekdayMismatch')}
            </p>
          )}
        </section>
      )}

      {/* 今週預覽（即時計，未產生／已確認都可以睇） */}
      {p && (
        <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
          <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 800 }}>
            {t('weeklyFee.currentWeek', { week: p.weekStart })}
          </h3>
          <p style={hintStyle}>
            {t('weeklyFee.openingCbm')}：<span style={mono}>{fmt(p.openingCbm, 6)}</span>
            {'　'}
            {t('weeklyFee.outboundCbm')}：<span style={mono}>{fmt(p.outboundCbm, 6)}</span>
            {'　'}
            {t('weeklyFee.closingCbm')}：<span style={mono}>{fmt(p.closingCbm, 6)}</span>
          </p>
          <p style={hintStyle}>
            {t('weeklyFee.storageAmount')}：<span style={mono}>${fmt(p.storageAmount, 2)}</span>
            {'　'}
            {t('weeklyFee.handlingAmount')}：<span style={mono}>${fmt(p.handlingAmount, 2)}</span>
            {'　'}
            {t('weeklyFee.totalAmount')}：<span style={mono}>${fmt(p.totalAmount, 2)}</span>
          </p>
          {p.issues.length > 0 && (
            <p style={errText} role="alert">{t('weeklyFee.issues')}{p.issues.join('；')}</p>
          )}
          {canManage ? (
            <>
              <button type="button" style={btnPrimary} onClick={() => void generate()} disabled={busy}>
                {busy ? t('common.saving') : t('weeklyFee.generate')}
              </button>
              <p style={hintStyle}>{t('weeklyFee.generateHint')}</p>
            </>
          ) : (
            <p style={hintStyle}>{t('weeklyFee.readOnlyHint')}</p>
          )}
        </section>
      )}

      {/* ⭐ 逐個 SKU 嘅期初 cbm（P1.5-A：物流核對 cbm 用；加埋 ＝ 上面「期初 cbm」） */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>
          {t('weeklyFee.skuTitle')}
        </h3>
        <p style={hintStyle}>{t('weeklyFee.skuHint')}</p>
        {items.length === 0 ? (
          <p style={hintStyle}>{t('weeklyFee.skuEmpty')}</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '14px' }}>
              <thead>
                <tr>
                  {[t('weeklyFee.skuCode'), t('weeklyFee.skuName'), t('weeklyFee.skuBarcode'), t('weeklyFee.skuCartons'), t('weeklyFee.skuCbm')].map((h) => (
                    <th
                      key={h}
                      style={{
                        textAlign: 'left',
                        padding: '6px 8px',
                        borderBottom: '1px solid var(--color-divider)',
                        whiteSpace: 'nowrap',
                      }}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.itemId}>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                      {it.supplierCode}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                      {it.itemName}
                    </td>
                    <td style={{ padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                      {it.barcode}
                    </td>
                    <td style={{ ...mono, padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                      {it.cartons}
                    </td>
                    <td style={{ ...mono, padding: '6px 8px', borderBottom: '1px solid var(--color-divider)' }}>
                      {fmt(it.cbm, 6)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* 歷史週費 */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 8px', fontSize: '15px', fontWeight: 800 }}>
          {t('weeklyFee.historyTitle')}
        </h3>
        {data && data.fees.length === 0 ? (
          <p style={hintStyle}>{t('weeklyFee.empty')}</p>
        ) : (
          <>
            {data?.fees.map((f) => (
              <div
                key={f.id}
                style={{
                  padding: '10px 0',
                  borderTop: '1px solid var(--color-divider)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '8px',
                  alignItems: 'center',
                }}
              >
                <strong style={{ minWidth: '180px' }}>
                  {t('weeklyFee.weekLabel', { start: f.weekStart, end: f.weekEnd })}
                </strong>
                <span style={hintStyle}>
                  {t('weeklyFee.openingCbm')} <span style={mono}>{fmt(f.openingCbm, 6)}</span>
                </span>
                <span style={hintStyle}>
                  {t('weeklyFee.totalAmount')} <span style={mono}>${fmt(f.totalAmount, 2)}</span>
                </span>
                <span style={badge(f.status === 'confirmed' ? 'ok' : 'info')}>
                  {f.status === 'confirmed' ? t('weeklyFee.confirmed') : t('weeklyFee.draft')}
                </span>
                {f.status === 'draft' && canManage && (
                  <button
                    type="button"
                    style={btnPrimary}
                    onClick={() => void confirm(f)}
                    disabled={busy}
                  >
                    {t('weeklyFee.confirm')}
                  </button>
                )}
                {/* ⭐ P1.5-A：物流／內部「核對後確認」（唔改金額；記 who／幾時／備註） */}
                <button type="button" style={btnGhost} onClick={() => void ack(f)} disabled={busy}>
                  {t('weeklyFee.ack')}
                </button>
              </div>
            ))}
            {data && (
              <p style={{ ...hintStyle, marginTop: '8px', fontWeight: 700 }}>
                {t('weeklyFee.totals')}：${fmt(data.totals.storage, 2)} + ${fmt(data.totals.handling, 2)} ＝ $
                {fmt(data.totals.total, 2)}
              </p>
            )}
          </>
        )}
      </section>

      {/* 確認紀錄（讀 `audit_log`；P1.5-A —— 物流核對後嘅痕跡） */}
      <section style={{ ...card, padding: '16px', marginTop: '12px' }}>
        <h3 style={{ margin: '0 0 6px', fontSize: '15px', fontWeight: 800 }}>{t('weeklyFee.acksTitle')}</h3>
        <p style={hintStyle}>{t('weeklyFee.acksHint')}</p>
        {(data?.acks ?? []).length === 0 ? (
          <p style={hintStyle}>{t('weeklyFee.acksEmpty')}</p>
        ) : (
          (data?.acks ?? []).map((a) => (
            <div
              key={`${a.feeId}-${a.at}`}
              style={{ padding: '8px 0', borderTop: '1px solid var(--color-divider)', fontSize: '14px' }}
            >
              <strong>{a.weekStart ?? a.feeId}</strong>
              {'　'}
              <span style={hintStyle}>
                {a.by ?? '—'}　{a.at}
              </span>
              {a.note && <span style={hintStyle}>　「{a.note}」</span>}
            </div>
          ))
        )}
      </section>
    </>
  );
}
